import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { createDebug } from '../../utils/debugLog'
import { supabase } from '../../utils/supabase'
import {
  MIN_PASSWORD_LENGTH,
  PASSWORD_RULES,
  getPasswordRuleTranslationKey,
  translatePasswordError,
  validatePassword,
} from '../../utils/passwordPolicy'
import Modal from '../ui/Modal'

const log = createDebug('dw:account-self-service:change-password-modal')
const policyLog = createDebug('dw:auth-password-policy:change-password')

interface ChangePasswordModalProps {
  isOpen: boolean
  onClose: () => void
  email: string
  hasPasswordIdentity: boolean
}

const RATE_LIMIT_COOLDOWN_MS = 30_000

export default function ChangePasswordModal({
  isOpen,
  onClose,
  email,
  hasPasswordIdentity,
}: ChangePasswordModalProps) {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const { t } = useTranslation('auth')
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null)
  const cooldownTimer = useRef<ReturnType<typeof setInterval> | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!isOpen) {
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setError(null)
      setSuccess(false)
      setSubmitting(false)
      if (closeTimer.current) clearTimeout(closeTimer.current)
    } else {
      log('open hasPasswordIdentity=%s', hasPasswordIdentity)
    }
  }, [isOpen, hasPasswordIdentity])

  useEffect(() => {
    if (cooldownUntil === null) return
    cooldownTimer.current = setInterval(() => {
      if (cooldownUntil <= Date.now()) {
        setCooldownUntil(null)
        if (cooldownTimer.current) clearInterval(cooldownTimer.current)
      } else {
        // Force re-render to update remaining seconds.
        setCooldownUntil((c) => c)
      }
    }, 1000)
    return () => {
      if (cooldownTimer.current) clearInterval(cooldownTimer.current)
    }
  }, [cooldownUntil])

  const cooldownActive = cooldownUntil !== null && cooldownUntil > Date.now()
  const cooldownRemaining = cooldownActive
    ? Math.max(0, Math.ceil((cooldownUntil! - Date.now()) / 1000))
    : 0

  const newPwValidation = validatePassword(newPassword)
  const newPwInvalid = newPassword.length > 0 && !newPwValidation.ok
  const passwordsMismatch = confirmPassword.length > 0 && newPassword !== confirmPassword
  const formValid =
    !cooldownActive &&
    newPwValidation.ok &&
    confirmPassword === newPassword &&
    (!hasPasswordIdentity || currentPassword.length > 0)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formValid || submitting) return
    // Defensive: formValid already requires a compliant password, but re-check so
    // the policy is enforced even if the gate logic changes, and emit a tagged log.
    const validation = validatePassword(newPassword)
    if (!validation.ok) {
      policyLog('reject failedRules=%o', validation.failedRuleIds)
      setError(translatePasswordError(t, validation))
      return
    }
    setSubmitting(true)
    setError(null)
    log('submit start hasPasswordIdentity=%s', hasPasswordIdentity)
    try {
      if (hasPasswordIdentity) {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password: currentPassword,
        })
        if (signInError) {
          throw signInError
        }
      }
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      })
      if (updateError) {
        throw updateError
      }
      log('submit success')
      setSuccess(true)
      setSubmitting(false)
      closeTimer.current = setTimeout(() => {
        onClose()
      }, 1500)
    } catch (err) {
      const errObj = (err instanceof Object ? err : {}) satisfies object
      const status = 'status' in errObj && typeof errObj.status === 'number' ? errObj.status : undefined
      const message = err instanceof Error ? err.message : t('failedToUpdatePassword')
      log('submit error status=%s message=%s', status, message)
      if (status === 429) {
        setCooldownUntil(Date.now() + RATE_LIMIT_COOLDOWN_MS)
        log('rate-limit cooldown triggered ms=%d', RATE_LIMIT_COOLDOWN_MS)
        setError(t('tooManyRequests'))
      } else if (/reauthenticat/i.test(message)) {
        // GoTrue's secure-password-change requires a recent (<24h) session. The
        // re-sign-in above covers password-identity users, but an OAuth-only user
        // setting a password on a >24h-old social session lands here. Guide them
        // to refresh their session rather than showing GoTrue's raw message.
        policyLog('reauthentication required hasPasswordIdentity=%s', hasPasswordIdentity)
        setError(t('reauthenticationRequired'))
      } else {
        setError(message)
      }
      setSubmitting(false)
    }
  }

  const title = hasPasswordIdentity ? t('changePassword') : t('setPassword')
  const submitLabel = hasPasswordIdentity ? t('changePassword') : t('setPassword')

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" title={title}>
      <form
        className="flex flex-col gap-3"
        onSubmit={handleSubmit}
        data-testid="change-password-modal"
      >
        {!hasPasswordIdentity && (
          <p className="text-xs font-medium text-muted-foreground">
            {t('socialSignInInfo')}
          </p>
        )}
        {hasPasswordIdentity && (
          <div className="flex flex-col gap-1">
            <label htmlFor="current-password" className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t('currentPassword')}
            </label>
            <input
              id="current-password"
              data-testid="current-password-input"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="px-3 py-2 rounded-md bg-surface-2 border border-border text-sm text-foreground"
              required
            />
          </div>
        )}
        <div className="flex flex-col gap-1">
          <label htmlFor="new-password" className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t('newPassword')}
          </label>
          <input
            id="new-password"
            data-testid="new-password-input"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="px-3 py-2 rounded-md bg-surface-2 border border-border text-sm text-foreground"
            minLength={MIN_PASSWORD_LENGTH}
            required
          />
          <ul className="mt-1 space-y-0.5" data-testid="password-requirements">
            {PASSWORD_RULES.map((rule) => {
              const met = rule.test(newPassword)
              return (
                <li
                  key={rule.id}
                  data-testid={`password-rule-${rule.id}`}
                  data-met={met}
                  className={`text-xs font-medium flex items-center gap-1.5 ${met ? 'text-emerald-400' : 'text-muted-foreground'}`}
                >
                  <span aria-hidden>{met ? '✓' : '○'}</span>
                  {rule.id === 'length' ? t(getPasswordRuleTranslationKey(rule.id), { count: MIN_PASSWORD_LENGTH }) : t(getPasswordRuleTranslationKey(rule.id))}
                </li>
              )
            })}
          </ul>
          {newPwInvalid && (
            <span className="text-xs font-medium text-red-400">
              {translatePasswordError(t, newPwValidation)}
            </span>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="confirm-password" className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t('confirmNewPassword')}
          </label>
          <input
            id="confirm-password"
            data-testid="confirm-password-input"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="px-3 py-2 rounded-md bg-surface-2 border border-border text-sm text-foreground"
            required
          />
          {passwordsMismatch && (
            <span className="text-xs font-medium text-red-400">{t('passwordsDoNotMatch')}</span>
          )}
        </div>
        {error && (
          <div className="text-xs font-medium text-red-400" data-testid="change-password-error">
            {error}
          </div>
        )}
        {success && (
          <div className="text-xs font-medium text-emerald-400" data-testid="change-password-success">
            {t('passwordUpdated')}
          </div>
        )}
        <div className="flex justify-end gap-2 mt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 rounded-md text-sm bg-surface-2 hover:bg-surface-3 text-foreground transition-colors cursor-pointer"
          >
            {t('cancel')}
          </button>
          <button
            type="submit"
            data-testid="change-password-submit"
            disabled={!formValid || submitting || success}
            className="px-3 py-2 rounded-md text-sm bg-primary hover:bg-primary-dark text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
          >
            {submitting
              ? t('updating')
              : cooldownActive
                ? t('tryAgainInSeconds', { seconds: cooldownRemaining })
                : submitLabel}
          </button>
        </div>
      </form>
    </Modal>
  )
}
